import { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { getExistingShareData } from '../utils/cesiumTokenUtils';
import { encodeTransform } from '../viewer/utils/transformationCodec';
import { AppConfigContext } from '../context/AppConfigContext';

function CesiumTokenDialog({ 
  open, 
  onClose, 
  fileData,
  onCreateToken, 
  onDeleteToken
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { maxLinkCesium, usedCesiumLinks } = useContext(AppConfigContext);
  const [shareUrl, setShareUrl] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasExistingShare, setHasExistingShare] = useState(false);
  const [existingTokenId, setExistingTokenId] = useState(null);

  // Check for existing share data when dialog opens
  useEffect(() => {
    if (open && fileData) {
      const shareData = getExistingShareData(fileData);
      setHasExistingShare(shareData.hasExistingShare);
      
      if (shareData.hasExistingShare) {
        // Generate share URL from existing short URL
        const baseUrl = window.location.origin;
        const shortCode = shareData.shortUrlData?.shortCode;
        if (shortCode) {
          setShareUrl(`${baseUrl}/${shortCode}`);
        } else {
          // Fallback to direct URL if short URL data is incomplete - include gaussian splatting ID if available
          const fallbackUrl = fileData.attributes.id_cesium_gs 
            ? `${baseUrl}/public/cesium/${fileData.attributes.id_cesium}/${fileData.attributes.id_cesium_gs}/${shareData.tokenId}`
            : `${baseUrl}/public/cesium/${fileData.attributes.id_cesium}/${shareData.tokenId}`;
          setShareUrl(fallbackUrl);
        }
        setExistingTokenId(shareData.tokenId);
      }
    }
  }, [open, fileData]);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      enqueueSnackbar(t('LinkCopiedToClipboard'), { variant: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      enqueueSnackbar(t('FailedToCopyLink'), { variant: 'error' });
    }
  };

  const handleCreateToken = async () => {
    // Check if limit is reached before creating a new link (not when replacing existing)
    if (!hasExistingShare && usedCesiumLinks >= maxLinkCesium) {
      enqueueSnackbar(t('NoRemainingLinks'), { variant: 'warning' });
      return;
    }

    const resultsId = fileData.attributes.results.data
      .map(result => result.attributes.id_cesium)
      .filter(id => id != null); // Filter out null/undefined values

    const pointTransform = fileData.attributes.transformation_cesium_points?.data;
    const gsTransform    = fileData.attributes.transformation_cesium_gs?.data;

    setIsCreating(true);
    try {
      const result = await onCreateToken({
        name: fileData.attributes.filename,
        assetId: fileData.attributes.id_cesium,
        gaussianSplattingId: fileData.attributes.id_cesium_gs,
        resultsId: resultsId,
        transformationPointsStr: pointTransform ? encodeTransform(pointTransform.attributes) : null,
        transformationGsStr:     gsTransform    ? encodeTransform(gsTransform.attributes)    : null,
        scopes: ['assets:read']
      }, fileData.id);
      
      // Use short URL if available, otherwise fallback to full URL  
      const newShareUrl = result.shortCode
        ? `${window.location.origin}/${result.shortCode}`
        : result.fullUrl;
      
      setShareUrl(newShareUrl);
      setHasExistingShare(true);
      setExistingTokenId(result.id);
      
      enqueueSnackbar(t('ShareLinkCreatedSuccessfully'), { variant: 'success' });
    } catch (error) {
      console.error('Token creation error in dialog:', error);
      enqueueSnackbar(`${t('FailedToCreateToken')}: ${error.message}`, { variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteToken = async () => {
    if (!existingTokenId) return;
    
    setIsDeleting(true);
    try {
      await onDeleteToken(existingTokenId, fileData.id, fileData);
      
      setShareUrl('');
      setHasExistingShare(false);
      setExistingTokenId(null);
      
      enqueueSnackbar(t('ShareLinkDeletedSuccessfully'), { variant: 'success' });
    } catch (error) {
      console.error('Token deletion error in dialog:', error);
      enqueueSnackbar(`${t('FailedToDeleteToken')}: ${error.message}`, { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setCopied(false);
    setShareUrl('');
    setHasExistingShare(false);
    setExistingTokenId(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box sx={{ p: 3 }}>
        <DialogTitle>
          <Typography variant="h4" component="div">
            {hasExistingShare ? t('ShareLink') : t('CreateShareLink')}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 16,
            top: 16,
            color: theme.palette.grey[500],
          })}
        >
          <CancelIcon />
        </IconButton>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

            {/* Existing link section */}
            {hasExistingShare && shareUrl && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('ExistingShareLink')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    value={shareUrl}
                    variant="outlined"
                    size="small"
                    slotProps={{
                      input: {
                        readOnly: true,
                        style: { fontSize: '0.875rem' }
                      }
                    }}
                  />
                  <Tooltip title={copied ? t('Copied') : t('CopyToClipboard')}>
                    <IconButton 
                      onClick={handleCopyToClipboard}
                      color={copied ? "success" : "primary"}
                    >
                      {copied ? <CheckIcon /> : <ContentCopyIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('DeleteShareLink')}>
                    <IconButton 
                      onClick={handleDeleteToken}
                      color="error"
                      disabled={isDeleting}
                    >
                      {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
                {/* <Alert severity="info">
                  {t('ExistingShareLinkInfo')}
                </Alert> */}
              </Box>
            )}

            {/* No link section */}
            {!hasExistingShare && !isCreating && (
              <Alert severity="info">
                {t('NoShareLinkExists')}
              </Alert>
            )}

            {/* Creating state */}
            {isCreating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                <CircularProgress size={24} />
                <Typography>{t('CreatingShareLink')}</Typography>
              </Box>
            )}

            {/* Warning */}
            {(hasExistingShare || isCreating) && (
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>{t('Important')}:</strong> {t('ShareLinkCesiumWarning')}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          {!hasExistingShare && (
            <Button 
              onClick={handleCreateToken} 
              variant="contained" 
              disabled={isCreating}
            >
              {t('CreateShareLink')}
            </Button>
          )}
          {hasExistingShare && (
            <Button 
              onClick={handleCreateToken} 
              variant="outlined"
              disabled={isCreating}
            >
              {t('CreateNewLink')}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default CesiumTokenDialog;